import React, { FC, useEffect } from 'react';
import { UitkLayoutGrid, UitkLayoutGridItem, UitkLayoutGridItemProps } from 'uitk-react-layout-grid';
import { UitkLayoutPosition } from 'uitk-react-layout-position';
import { UitkSpacing, UitkSpacingProps } from 'uitk-react-spacing';
import { ViewExtraLarge, Viewport } from '@shared-ui/viewport-context';
import { ErrorBoundary } from 'bernie-view';
import { useBexApi } from '@shared-ui/bex-api-context';
import { GenericErrorMessage } from '../common/generic-error-message';
import useNavigationParams from '../../hooks/useNavigationParams';
import EgMarkdownRenderer from '@eg-markdown-renderer/core';
import TableOfContent from 'components/documentation-page/table-of-content';
import DocumentErrorView from './document-error-view';
import LoadingSkeleton from '../documentation-page/loading-skeleton';
import { BreadcrumbLinkFragment, BreadcrumbTextFragment, useDocumentQuery } from '../../graphql/__generated__/types';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import DocumentationNavigator from '../documentation-navigator/documentation-navigator';
import RelatedSection from '../related-section/related-section';
import Footer from '../footer';
import MessageBanner from '../common/message-banner';
import useAnalytics from '../../hooks/analytics/useAnalytics';
import ContentFeedback from '../content-feedback/content-feedback';
import CustomerSurvey from '../customer-survey/customer-survey';
import { useMarkdownRendererOptions } from 'hooks/useMarkdownRendererOptions';
import { Breadcrumbs } from '../breadcrumbs/breadcrumbs';
import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import useMessageBanner from 'hooks/useMessageBanner';
import SurveyTrigger from '../survey-trigger/survey-trigger';
import useSideBarVisibility from '../../hooks/useSideBarVisiblity';
// TODO: Add this back when we want to use the GraphQL playground component, commented out for now to avoid breaking SSR
// import '@eg-markdown-renderer/core/dist/external-styles.css';
import HeaderTitle from '../header-title';

const DocumentView: FC = () => {
  const { hubId, hash, documentId } = useNavigationParams();
  const { context } = useBexApi();
  const { setMessageBanner } = useMessageBanner();
  const { data, loading, error } = useDocumentQuery({ variables: { context, hubId, documentId } });
  const analytics = useAnalytics();
  const { componentsOptions, contentVersion } = useMarkdownRendererOptions(hubId);

  const { hideSideBar } = useSideBarVisibility();

  useEffect(() => {
    if (!hash) document?.getElementById('document')?.scrollTo(0, 0);
  }, [documentId]);

  const documentTypeName = data?.documentation?.document?.__typename;
  React.useEffect(() => {
    if (
      !loading &&
      documentTypeName !== 'DocumentationDocumentRedirect' &&
      data?.documentation?.document?.notifications !== null
    ) {
      setMessageBanner({
        message: data.documentation?.document?.notifications[0]?.message,
        iconName: data.documentation?.document?.notifications[0]?.icon.token,
        messageStyle: { marginLeft: '1rem' },
      });
    }
  }, [data?.documentation?.document]);

  useEffect(() => {
    if (!loading && !error && documentTypeName === 'DocumentationDocumentSuccessResponse') {
      analytics.trackPageView();
    }
  }, [documentId, loading, error]);

  if (error || documentTypeName === 'DocumentationDocumentRedirect') {
    return <GenericErrorMessage />;
  }

  if (loading || !data) {
    return (
      <UitkLayoutGrid columns={10}>
        <UitkLayoutGridItem {...documentGridColumnProps}>
          <UitkSpacing {...documentContainerSpacingProps}>
            <div>
              <LoadingSkeleton />
            </div>
          </UitkSpacing>
        </UitkLayoutGridItem>
      </UitkLayoutGrid>
    );
  }

  if (documentTypeName === 'DocumentationDocumentErrorResponse') {
    return <DocumentErrorView errorResponse={data.documentation.document} />;
  }

  const { content, banner, title, intro, navigationTrail } = data.documentation.document;
  return (
    <UitkLayoutGrid columns={10} style={{ flexGrow: 1 }}>
      <UitkLayoutGridItem {...documentGridColumnProps}>
        <UitkSpacing {...documentContainerSpacingProps}>
          <div>
            <Experiment name="DevHub_Breadcrumbs">
              <ExperimentControl>
                <></>
              </ExperimentControl>
              <ExperimentVariant bucket={1}>
                <Breadcrumbs crumbs={navigationTrail as (BreadcrumbTextFragment | BreadcrumbLinkFragment)[]} />
              </ExperimentVariant>
            </Experiment>
            <MessageBanner
              message={banner?.message}
              iconName={banner?.icon.token}
              spacing={{ margin: { blockend: 'four' } }}
            />
            <ErrorBoundary label="document.body" key={documentId} fallback={<GenericErrorMessage />}>
              <div className="md-renderer-wrapper">
                <EgMarkdownRenderer mdBody={content} componentsOptions={componentsOptions} />
              </div>
            </ErrorBoundary>
            <UitkSpacing margin={{ block: 'twelve' }}>
              <div>
                <RelatedSection />
              </div>
            </UitkSpacing>
            <UitkSpacing margin={{ block: 'twelve' }}>
              <div>
                <ContentFeedback />
              </div>
            </UitkSpacing>
            {!hideSideBar ? (
              <UitkSpacing {...navigatorSpacingProps}>
                <div>
                  <DocumentationNavigator />
                </div>
              </UitkSpacing>
            ) : null}
          </div>
        </UitkSpacing>
      </UitkLayoutGridItem>
      <Viewport>
        <ViewExtraLarge>
          <UitkLayoutGridItem colSpan={2} alignSelf="start">
            <UitkLayoutPosition type="sticky" position={{ top: 'six' }}>
              <TableOfContent contentVersion={contentVersion} />
            </UitkLayoutPosition>
          </UitkLayoutGridItem>
        </ViewExtraLarge>
      </Viewport>
      <UitkLayoutGridItem {...footerGridColumnProps} style={{ alignSelf: 'end' }}>
        <UitkLayoutFlex direction="column">
          <UitkLayoutFlexItem>
            <div>
              <Footer />
            </div>
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      </UitkLayoutGridItem>

      <Experiment name="DevHub_Feedback_Form">
        <ExperimentControl>
          {!hubId.includes('mock') ? (
            <UitkLayoutPosition type="absolute" position={{ bottom: 'twelve', right: 'twelve' }}>
              <SurveyTrigger />
            </UitkLayoutPosition>
          ) : null}
        </ExperimentControl>
        <ExperimentVariant bucket={1}>{!hubId.includes('mock') ? <CustomerSurvey /> : null}</ExperimentVariant>
      </Experiment>

      <HeaderTitle documentTitle={title} content={intro} />
    </UitkLayoutGrid>
  );
};

const documentGridColumnProps: UitkLayoutGridItemProps = {
  colSpan: { small: 'all' },
  colStart: { extra_large: 2, small: 1 },
  colEnd: { extra_large: 8, small: 12 },
  className: 'large-max-width',
};

const footerGridColumnProps: UitkLayoutGridItemProps = {
  ...documentGridColumnProps,
  colSpan: 'all',
  colStart: 2,
  rowStart: 0,
  rowEnd: 0,
};
const documentContainerSpacingProps: UitkSpacingProps = {
  margin: { large: { block: 'six' } },
  padding: {
    small: { inline: 'four', block: 'three' },
    large: { block: 'three' },
    extra_large: { inline: 'unset' },
  },
};
const navigatorSpacingProps: UitkSpacingProps = {
  padding: { blockend: 'six' },
};
export default DocumentView;
